/**
 * The content a prerendered page carries with it.
 *
 * The browser cannot read `content/posts/` — that is the BUILD's job — so each
 * page embeds the content it was rendered from, and this module reads it back
 * before the tree renders. Without it React would hydrate against an empty
 * content slot and throw the server markup away, leaving a blank list.
 *
 * WHAT IS EMBEDDED, and what is not. The payload is a SUBSET of the content
 * model: exactly the fields a page renders. Two fields are deliberately left
 * out, and both matter:
 *
 *   - `markdown` — each post's raw source, ~48 KB of it per page. Only the
 *     build reads it (to publish `/<slug>.md`), and shipping it to the browser
 *     put raw markdown — backticks and all — into every page's HTML, where it
 *     is not prose the reader sees and should never appear.
 *   - `provenance` — the per-paragraph map, ~9 KB. The paragraphs already carry
 *     their own `origin`/`prompt`/`model`, which is what the page renders; the
 *     ordered map is published as `/<slug>/provenance.json` for crawlers.
 *
 * Everything else is kept, because a page renders it.
 */

import type { Post, PostSection, SiteContent } from './content-types.js'

/** The element id the prerender writes and this module looks for. */
export const EMBEDDED_CONTENT_ID = 'mol-site-content'

/** One post as the CLIENT receives it: no raw markdown, no ordered map. */
export type EmbeddedPost = Omit<Post, 'markdown' | 'provenance'>

/** The content model a page embeds — every post minus its build-only fields. */
export interface EmbeddedContent {
  posts: EmbeddedPost[]
}

/**
 * Reduce the build's content model to what a page actually renders.
 *
 * @param content - The posts the page was rendered from.
 * @returns The same posts, without the fields only the build uses.
 */
export function toEmbedded(content: SiteContent): EmbeddedContent {
  return {
    posts: content.posts.map((post) => {
      const { markdown: _markdown, provenance: _provenance, ...embedded } = post
      return embedded
    }),
  }
}

/**
 * Read the content embedded in the current document.
 *
 * @returns The posts the page was rendered from, or an empty site when the
 *   element is absent (a dev-server route with no content, a unit test).
 */
export function readEmbeddedContent(): SiteContent {
  if (typeof document === 'undefined') return { posts: [] }
  const element = document.getElementById(EMBEDDED_CONTENT_ID)
  if (!element?.textContent) return { posts: [] }
  try {
    const parsed = JSON.parse(element.textContent) as unknown
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as SiteContent).posts)) {
      return parsed as SiteContent
    }
  } catch (error) {
    // A malformed payload is a page that renders without its list — never a
    // thrown error that takes the whole app down.
    console.warn('[static-site] the embedded content could not be read', error)
  }
  return { posts: [] }
}

export type { Post, PostSection, SiteContent }
